/* eslint-disable jsx-a11y/aria-role */
import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import getTextContent from '../../utils/getTextContent'

/**
 * Bronson PriceBox component.
 * @see https://bronson.vwfs.tools/bluelabel/components/detail/bronson-price-box.html
 *
 * @desc Please note: In order to announce the discount value, discount price
 *       and actual price to assistive technology, apply both `[aria-label]` and
 *       `[role=text]` to those elements. This ensures accessible content without
 *       relying on the visual appearance.
 *
 * @param {React.ReactNode} price - Price text.
 * @param {string} ariaLabelPrice - Accessible name for the price text.
 * @param {boolean} [standalone] - Handles whether the {@link PriceBox} element is standalone
 *                                 or can be visually integrated in other components.
 * @param {React.ReactNode} [header] - Header text.
 * @param {React.ReactNode} [discountValue] - Discount label text.
 * @param {string} [ariaLabelDiscountValue] - Accessible name for the discount label text.
 * @param {React.ReactNode} [discountOldPrice] - Discount price text.
 * @param {string} [ariaLabelDiscountOldPrice] - Accessible name for the discount price text.
 * @param {React.ReactNode} [textAbovePrice] - Text which is displayed above the price.
 * @param {React.ReactNode} [textBelowPrice] - Text which is displayed below the price.
 * @param {React.ReactNode} [additionalInfo] - Additional information text (e.g. tax information).
 * @param {string} [testId] - The `testId` string.
 * @param {object} [otherProps] - Other props to pass on the {@link PriceBox} element.
 * @return {JSX.Element} - The {@link PriceBox} element.
 * @constructor
 */
export function PriceBox({
  standalone,
  header,
  discountValue,
  ariaLabelDiscountValue,
  discountOldPrice,
  ariaLabelDiscountOldPrice,
  price,
  ariaLabelPrice,
  textAbovePrice,
  textBelowPrice,
  additionalInfo,
  testId,
  ...otherProps
}) {
  const CustomTag = `${standalone ? 'article' : 'div'}`
  const CustomTagHeader = `${standalone ? 'header' : 'div'}`

  const classNameList = classNames('c-price-box ', { 'c-price-box--integrated ': !standalone }).trim()
  const tagClassNameList = classNames('c-tag ', 'c-tag--negative ', 'c-price-box__discount__value ').trim()

  function renderHeader() {
    if (header) {
      return <CustomTagHeader className="c-price-box__header">{header}</CustomTagHeader>
    }
    return null
  }

  function renderDiscountValue() {
    if (discountValue) {
      return (
        <span className={tagClassNameList}>
          <span
            className="c-tag__label"
            aria-label={`${ariaLabelDiscountValue} ${getTextContent(discountValue)}`}
            role="text"
          >
            {discountValue}
          </span>
        </span>
      )
    }
    return null
  }

  function renderDiscountOldPrice() {
    if (discountOldPrice) {
      return (
        <s
          className="c-price-box__discount__old-price"
          aria-label={`${ariaLabelDiscountOldPrice} ${getTextContent(discountOldPrice)}`}
          role="text"
        >
          {discountOldPrice}
        </s>
      )
    }
    return null
  }

  function renderDiscount() {
    if (discountValue || discountOldPrice) {
      return (
        <div className="c-price-box__discount">
          {renderDiscountValue()}
          {renderDiscountOldPrice()}
        </div>
      )
    }
    return null
  }

  function renderPrice() {
    return (
      <span className="c-price-box__price" aria-label={`${ariaLabelPrice} ${getTextContent(price)}`} role="text">
        {price}
      </span>
    )
  }

  function renderTextAbovePrice() {
    if (textAbovePrice) {
      return <span className="c-price-box__text">{textAbovePrice}</span>
    }
    return null
  }

  function renderTextBelowPrice() {
    if (textBelowPrice) {
      return <span className="c-price-box__text">{textBelowPrice}</span>
    }
    return null
  }

  function renderAdditionalInfo() {
    if (additionalInfo) {
      return (
        <div className="c-price-box__additional-info">
          <p className="c-price-box__additional-info__text">{additionalInfo}</p>
        </div>
      )
    }
    return null
  }

  return (
    <CustomTag {...otherProps} data-testid={testId} className={classNameList}>
      {renderHeader()}
      <div className="c-price-box__body">
        <div className="c-price-box__body__content">
          {renderTextAbovePrice()}
          {renderDiscount()}
          {renderPrice()}
          {renderTextBelowPrice()}
        </div>
        {renderAdditionalInfo()}
      </div>
    </CustomTag>
  )
}

PriceBox.propTypes = {
  standalone: PropTypes.bool, // Bronson template: 'standalone'.
  header: PropTypes.node, // Bronson template: 'header'.
  discountValue: PropTypes.node, // Bronson template: 'discount.value.value'.
  ariaLabelDiscountValue: (props, propName, componentName, ...rest) => {
    if (!props[propName] && props.discountValue !== undefined) {
      return new Error('The prop `ariaLabelDiscountValue` is required if `discountValue` is not `undefined`.')
    }
    return PropTypes.string(props, propName, componentName, ...rest)
  }, // Bronson template: 'discount.value.aria-label'.
  discountOldPrice: PropTypes.node, // Bronson template: 'discount.price.value'.
  ariaLabelDiscountOldPrice: (props, propName, componentName, ...rest) => {
    if (!props[propName] && props.discountOldPrice !== undefined) {
      return new Error('The prop `ariaLabelDiscountOldPrice` is required if `discountOldPrice` is not `undefined`.')
    }
    return PropTypes.string(props, propName, componentName, ...rest)
  }, // Bronson template: 'discount.price.aria-label'.
  price: PropTypes.node.isRequired, // Bronson template: 'price.value'.
  ariaLabelPrice: PropTypes.string.isRequired, // Bronson template: 'price.aria-label'.
  textAbovePrice: PropTypes.node, // Bronson template: 'text-above-price'.
  textBelowPrice: PropTypes.node, // Bronson template: 'text-below-price'.
  additionalInfo: PropTypes.node, // Bronson template: 'additional-info'.
  testId: PropTypes.string, // Added for `[data-testid]` attribute.
}
